import i18next from 'i18next';
import { of } from 'rxjs';
import { concatMap, tap, map, catchError } from 'rxjs/operators';
import { RxUtils, TypeUtils, CrudServiceBase } from '@utils';
import { httpService, logger, notificationService } from '@services';
import { PackageDeal } from './package-deal.class';

type Entity = PackageDeal;

export class PackageDealService extends CrudServiceBase<Entity> {
	constructor() {
		super(PackageDeal, 'package-deal');
	}

	create(entity: Partial<Entity>, logo?: File) {
		return super.create(entity).pipe(concatMap((result) => (logo ? this.uploadLogo(result, logo) : of(result))));
	}

	update(id: string, entity: Partial<Entity>, logo?: File) {
		return super.update(id, entity).pipe(concatMap((result) => (logo ? this.uploadLogo(result, logo) : of(result))));
	}

	uploadLogo(entity: Entity, logo?: File) {
		const formData = new FormData();
		logo && formData.append('file', logo, logo.name);
		return httpService
			.post<unknown>(this.getUrl(), `${entity.id}/image`, { body: formData, headers: { 'Content-Type': 'multipart/form-data' } })
			.pipe(
				tap((result) => logger.debug(`[${this.constructor.name}.${this.uploadLogo.name}]`, result)),
				map(() => entity),
				catchError(() => {
					notificationService.sendError(i18next.t('common.files-upload-failed'));
					return of(entity);
				})
			);
	}

	getLogo(entity: Entity) {
		return httpService.get<string>(this.getUrl(), `${entity.id}/image`).pipe(
			tap((result) => logger.debug(`[${this.constructor.name}.${this.getLogo.name}]`, result)),
			catchError(() => of(undefined))
		);
	}

	setSelected(id: string, selected: boolean) {
		return httpService.patch<unknown>(this.getUrl(), `${id}/select`, { body: { selected }, errorHandler: 'notification' }).pipe(
			tap((result) => logger.debug(`[${this.constructor.name}.${this.update.name}]`, result)),
			concatMap((result) => RxUtils.valueOrThrow(result, new Error('Response is empty'))),
			map((result) => TypeUtils.convert(PackageDeal, result))
		);
	}
}

export const packageDealService = new PackageDealService();
